import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { Crumb } from './personalTrail';

export interface PageHeaderState {
  actions?: ReactNode;
  /**
   * Optional page-built breadcrumb trail. Only the personal-edition header
   * consumes this; professional rendering keeps using route-context trails.
   */
  breadcrumbs?: Crumb[];
  description?: ReactNode;
  title: string;
}

interface PageHeaderContextValue {
  header: PageHeaderState | null;
  setHeader: (header: PageHeaderState | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeaderState | null>(null);

  // Stable setter so consumers can list it in effect deps without churning.
  const stableSetHeader = useCallback((next: PageHeaderState | null) => setHeader(next), []);

  // Only `header` change triggers re-renders; the setter ref is stable.
  const value = useMemo<PageHeaderContextValue>(
    () => ({ header, setHeader: stableSetHeader }),
    [header, stableSetHeader],
  );

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/**
 * Read the current page header. Throws if rendered outside a provider so
 * accidental misuse fails loudly. The provider lives at the app root in
 * `main.tsx`, above `<RouterProvider>`, so every route component sees it
 * regardless of how the router stitches matches together.
 */
export function usePageHeader(): PageHeaderContextValue {
  const ctx = useContext(PageHeaderContext);
  if (ctx === null) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider');
  }
  return ctx;
}

export function usePageHeaderSetter(): PageHeaderContextValue['setHeader'] {
  return usePageHeader().setHeader;
}
